import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, Select, MenuItem, FormControl, InputLabel,
  Checkbox, FormControlLabel, Typography, Grid, Divider,
  makeStyles, CircularProgress, IconButton, Box, Chip
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import { PERMISSIONS, ROLES } from '../../config/permissions';

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    borderRadius: 20,
    background: theme.palette.type === 'dark'
      ? 'linear-gradient(145deg, #0f172a 0%, #1a1f35 100%)'
      : 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
    border: theme.palette.type === 'dark'
      ? '1px solid rgba(148,163,184,0.1)'
      : '1px solid rgba(45,42,36,0.08)',
    boxShadow: '0 25px 60px rgba(0,0,0,0.3)',
    maxWidth: 640,
    width: '100%',
  },
  dialogTitle: {
    padding: '24px 28px 0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  titleText: {
    fontWeight: 700,
    fontSize: '1.15rem',
    color: theme.palette.text.primary,
    fontFamily: '"Plus Jakarta Sans", "Inter", sans-serif',
  },
  closeBtn: {
    color: theme.palette.text.disabled,
    '&:hover': { color: theme.palette.text.secondary },
  },
  dialogContent: {
    padding: '20px 28px',
  },
  sectionTitle: {
    fontSize: '0.72rem',
    fontWeight: 700,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: theme.palette.text.disabled,
    marginBottom: 10,
    marginTop: 4,
  },
  inputField: {
    '& .MuiInputBase-root': {
      borderRadius: 10,
      backgroundColor: theme.palette.type === 'dark'
        ? 'rgba(30,41,59,0.6)'
        : 'rgba(248,250,252,0.8)',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.type === 'dark'
        ? 'rgba(148,163,184,0.15)'
        : 'rgba(45,42,36,0.12)',
      borderRadius: 10,
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#10b981',
      borderWidth: 1,
    },
  },
  selectField: {
    borderRadius: 10,
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.type === 'dark'
        ? 'rgba(148,163,184,0.15)'
        : 'rgba(45,42,36,0.12)',
      borderRadius: 10,
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#10b981',
      borderWidth: 1,
    },
  },
  permissionBox: {
    background: theme.palette.type === 'dark'
      ? 'rgba(15,23,42,0.5)'
      : 'rgba(248,250,252,0.8)',
    border: theme.palette.type === 'dark'
      ? '1px solid rgba(148,163,184,0.1)'
      : '1px solid rgba(45,42,36,0.08)',
    borderRadius: 12,
    padding: '16px 20px',
    maxHeight: 260,
    overflowY: 'auto',
  },
  groupLabel: {
    fontSize: '0.72rem',
    fontWeight: 700,
    color: '#10b981',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    marginBottom: 4,
    marginTop: 12,
    '&:first-child': { marginTop: 0 },
  },
  checkLabel: {
    '& .MuiFormControlLabel-label': {
      fontSize: '0.85rem',
      color: theme.palette.text.secondary,
    },
  },
  permActionsRow: {
    display: 'flex',
    gap: 8,
    marginBottom: 10,
  },
  permChip: {
    fontSize: '0.72rem',
    height: 26,
    cursor: 'pointer',
    borderColor: 'rgba(16,185,129,0.4)',
    color: '#10b981',
    '&:hover': {
      background: 'rgba(16,185,129,0.1)',
    },
  },
  divider: {
    backgroundColor: theme.palette.type === 'dark'
      ? 'rgba(148,163,184,0.08)'
      : 'rgba(45,42,36,0.06)',
    margin: '16px 0',
  },
  dialogActions: {
    padding: '16px 28px 24px',
    gap: 10,
  },
  cancelBtn: {
    borderRadius: 10,
    textTransform: 'none',
    fontWeight: 600,
    color: theme.palette.text.secondary,
    borderColor: theme.palette.type === 'dark'
      ? 'rgba(148,163,184,0.2)'
      : 'rgba(45,42,36,0.15)',
    '&:hover': {
      borderColor: 'rgba(16,185,129,0.4)',
      color: theme.palette.text.primary,
    },
  },
  saveBtn: {
    borderRadius: 10,
    textTransform: 'none',
    fontWeight: 600,
    minWidth: 110,
    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
    color: '#fff',
    '&:hover': {
      background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
    },
    '&:disabled': { opacity: 0.6 },
  },
  errorBox: {
    background: 'rgba(239,68,68,0.08)',
    border: '1px solid rgba(239,68,68,0.2)',
    borderRadius: 8,
    padding: '10px 14px',
    marginBottom: 12,
  },
  errorText: {
    fontSize: '0.82rem',
    color: '#f87171',
  },
}));

// Build group map from PERMISSIONS
function buildGroups() {
  const groups = {};
  PERMISSIONS.forEach((p) => {
    if (!groups[p.group]) groups[p.group] = [];
    groups[p.group].push(p);
  });
  return groups;
}

const GROUPS = buildGroups();
const ALL_KEYS = PERMISSIONS.map((p) => p.key);

const EMPTY_FORM = {
  name: '',
  email: '',
  password: '',
  role: 'editor',
  permissions: ['dashboard'],
};

export default function AdminUserFormModal({ open, onClose, onSave, editData }) {
  const classes = useStyles();
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const isEdit = Boolean(editData);

  useEffect(() => {
    if (open) {
      if (editData) {
        setForm({
          name: editData.name || '',
          email: editData.email || '',
          password: '', // blank — only set if changing
          role: editData.role || 'editor',
          permissions: Array.isArray(editData.permissions) ? editData.permissions : ['dashboard'],
        });
      } else {
        setForm(EMPTY_FORM);
      }
      setFormError('');
    }
  }, [open, editData]);

  const handleField = (field) => (e) => {
    const val = e.target.value;
    setForm((prev) => {
      const next = { ...prev, [field]: val };
      if (field === 'role' && val === 'admin') {
        next.permissions = [...ALL_KEYS];
      }
      return next;
    });
  };

  const togglePermission = (key) => {
    if (form.role === 'admin') return;
    setForm((prev) => {
      const has = prev.permissions.includes(key);
      // dashboard is always included
      if (key === 'dashboard' && has) return prev;
      return {
        ...prev,
        permissions: has
          ? prev.permissions.filter((k) => k !== key)
          : [...prev.permissions, key],
      };
    });
  };

  const selectAll = () => {
    if (form.role === 'admin') return;
    setForm((prev) => ({ ...prev, permissions: [...ALL_KEYS] }));
  };

  const deselectAll = () => {
    if (form.role === 'admin') return;
    setForm((prev) => ({ ...prev, permissions: ['dashboard'] }));
  };

  const handleSave = async () => {
    setFormError('');
    if (!form.name.trim()) return setFormError('Name is required.');
    if (!form.email.trim()) return setFormError('Email is required.');
    if (!isEdit && !form.password.trim()) return setFormError('Password is required for new admins.');
    if (form.role !== 'admin' && form.permissions.length === 0) {
      return setFormError('At least one permission is required.');
    }

    setSaving(true);
    try {
      await onSave(form, editData?.id);
      onClose();
    } catch (err) {
      setFormError(err.message || 'Failed to save admin.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      classes={{ paper: classes.dialogPaper }}
    >
      <div className={classes.dialogTitle}>
        <Typography className={classes.titleText}>
          {isEdit ? 'Edit Admin User' : 'Add New Admin User'}
        </Typography>
        <IconButton size="small" className={classes.closeBtn} onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </div>

      <DialogContent className={classes.dialogContent}>
        {formError && (
          <div className={classes.errorBox}>
            <Typography className={classes.errorText}>{formError}</Typography>
          </div>
        )}

        {/* Basic Info */}
        <Typography className={classes.sectionTitle}>Basic Information</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              label="Full Name"
              value={form.name}
              onChange={handleField('name')}
              className={classes.inputField}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth variant="outlined" size="small">
              <InputLabel>Role</InputLabel>
              <Select
                value={form.role}
                onChange={handleField('role')}
                label="Role"
                className={classes.selectField}
              >
                {ROLES.map((r) => (
                  <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              label="Email Address"
              type="email"
              value={form.email}
              onChange={handleField('email')}
              className={classes.inputField}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              label={isEdit ? 'New Password (leave blank to keep current)' : 'Password'}
              type="password"
              value={form.password}
              onChange={handleField('password')}
              className={classes.inputField}
            />
          </Grid>
        </Grid>

        {form.role !== 'admin' && (
          <>
            <Divider className={classes.divider} />

            {/* Permissions */}
            <Typography className={classes.sectionTitle}>Section Permissions</Typography>
            <div className={classes.permActionsRow}>
              <Chip
                variant="outlined"
                size="small"
                label="Select All"
                icon={<CheckBoxIcon style={{ fontSize: 14 }} />}
                className={classes.permChip}
                onClick={selectAll}
              />
              <Chip
                variant="outlined"
                size="small"
                label="Deselect All"
                icon={<CheckBoxOutlineBlankIcon style={{ fontSize: 14 }} />}
                className={classes.permChip}
                onClick={deselectAll}
              />
              <Box flexGrow={1} />
              <Typography style={{ fontSize: '0.75rem', color: '#10b981', alignSelf: 'center' }}>
                {form.permissions.length} / {ALL_KEYS.length} selected
              </Typography>
            </div>

            <div className={classes.permissionBox}>
              {Object.entries(GROUPS).map(([groupName, items]) => (
                <div key={groupName}>
                  <Typography className={classes.groupLabel}>{groupName}</Typography>
                  <Grid container spacing={0}>
                    {items.map((perm) => (
                      <Grid item xs={6} key={perm.key}>
                        <FormControlLabel
                          className={classes.checkLabel}
                          control={
                            <Checkbox
                              size="small"
                              checked={form.permissions.includes(perm.key)}
                              onChange={() => togglePermission(perm.key)}
                              disabled={perm.key === 'dashboard'}
                              style={{ color: form.permissions.includes(perm.key) ? '#10b981' : undefined }}
                            />
                          }
                          label={perm.label}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </div>
              ))}
            </div>
          </>
        )}
      </DialogContent>

      <DialogActions className={classes.dialogActions}>
        <Button variant="outlined" className={classes.cancelBtn} onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button className={classes.saveBtn} onClick={handleSave} disabled={saving}>
          {saving ? <CircularProgress size={18} style={{ color: '#fff' }} /> : (isEdit ? 'Save Changes' : 'Create Admin')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
